import {
  Component,
  OnInit,
  Inject,
  AfterViewInit,
  ChangeDetectorRef,
  OnDestroy,
} from '@angular/core';
import {
  Router,
  RouteConfigLoadStart,
  NavigationCancel,
  NavigationEnd,
  RoutesRecognized,
} from '@angular/router';
import { DomSanitizer } from '@angular/platform-browser';
import { trigger, style, transition, animate } from '@angular/animations';
import {
  Observable,
  BehaviorSubject,
  combineLatest,
  Subscription,
  Subject,
  fromEvent,
} from 'rxjs';
import { map, filter, startWith, distinctUntilChanged } from 'rxjs/operators';
import { User } from 'oidc-client';
import {
  Navigation,
  ENVIRONMENT_CONFIG,
  EnvironmentConfig,
  getDevServersState,
  DevServersState,
  DevServer,
  RouteManifest,
  FeaturesState,
} from '@ipreo/northstar';
import {
  AuthService,
  getFeaturesState,
  RoutingService,
  NavigationService,
} from '@ipreo/northstar';
import { Store, select } from '@ngrx/store';
import { State } from './state/reducer';
import {
  HeaderItemType,
  HeaderGroup,
  HeaderItem,
  HeaderLinkParams,
} from './shared/header/header.component';
import { environment } from '../environments/environment';
interface DevServerMenuItem {
  name: string;
  env: 'local' | 'qx' | 'uat' | 'prod';
  releaseVersion: string;
}

// Routes that should be excluded from server menu processing
const EXCLUDED_ROUTES = ['/user-settings'];

@Component({
  selector: 'polaris-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  animations: [
    trigger('fadeIn', [
      transition(':enter', [
        style({
          opacity: '0',
        }),
        animate(
          '500ms ease-in',
          style({
            opacity: '1',
          })
        ),
      ]),
    ]),
    trigger('fadeOut', [
      transition(':leave', [
        style({
          opacity: '1',
        }),
        animate(
          '500ms ease-in',
          style({
            opacity: '0',
          })
        ),
      ]),
    ]),
  ],
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  public title = 'home';
  public isLoading: Observable<boolean>;
  public currentFooterLabel = 'Public';
  public showClassificationModal = false;

  public footerLinks: Array<{ name: string; href?: string; openInNewTab?: boolean }>;

  public user = new BehaviorSubject<User>(null);
  public token = new BehaviorSubject<string>(null);

  public serverInfo = new Subject<HeaderItem>();

  public headerDropdownSubmenu: HeaderLinkParams[] = [
    // {
    //   event: () => this.openUserSettings(),
    //   name: 'User Settings',
    // },
    {
      event: () =>
        window.open(
          'https://www.spglobal.com/en/legal/gcp-products-terms-of-use',
          '_blank'
        ),
      name: 'Terms of Use',
    },
    {
      event: () =>
        window.open('https://www.spglobal.com/en/legal/privacy', '_blank'),
      name: 'Privacy Policy',
    },
    {
      event: () =>
        window.open(
          `${window.location.origin}/legal/disclaimers.html`,
          '_blank'
        ),
      name: 'Disclaimers',
    },
    {
      event: () =>
        window.open(
          `${window.location.origin}/legal/third-party-data.html`,
          '_blank'
        ),
      name: 'Third Party Data',
    },
    {
      event: () => this.logout(),
      name: 'Logout',
    },
  ];
  public clearServerDropdown: HeaderItem;

  public headerItems: Observable<HeaderItem[]> = combineLatest([
    this.polarisNavigation.items.pipe(startWith([] as Navigation[])),
    this.user,
    this.serverInfo,
  ]).pipe(
    map(([navigationItems, user, serverInfo]) => {
      const linkNavItems = navigationItems.filter((item) => item.path !== '/');

      const navItems: HeaderItem[] =
        linkNavItems.length === 1
          ? this.convertNavToHeaderItems(linkNavItems[0].children)
          : this.convertNavToHeaderItems(linkNavItems);

      if (this.config.northstar.env !== 'prod' && serverInfo) {
        navItems.push(serverInfo);
      }

      if (user) {
        const { name, email } = user.profile;

        navItems.push({
          type: HeaderItemType.user,
          group: HeaderGroup.right,
          params: {
            name,
            email,
            menuItems: this.headerDropdownSubmenu,
          },
        });
      }

      return navItems;
    })
  );

  public navigation = this.polarisNavigation.items;

  public loading: Observable<boolean> = this.store.pipe(
    select((state) => !getFeaturesState(state).loaded)
  );

  public serverInfoObs: Observable<DevServer[]> = this.store.pipe(
    select(getDevServersState),
    map((state: DevServersState) =>
      state.servers.filter((server: DevServer) => server.enabled)
    ),
    distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
  );

  private featureRoutes: Observable<RouteManifest[]> = this.store.pipe(
    select(getFeaturesState),
    filter((state: FeaturesState) => state.loaded),
    map((state: FeaturesState) => state.routes)
  );

  private serverInfoSub: Subscription;
  private browserBackSub: Subscription;

  public cupcakeMajorVersion: number;

  constructor(
    private auth: AuthService,
    private polarisRouting: RoutingService,
    private polarisNavigation: NavigationService,
    private store: Store<State>,
    private router: Router,
    private sanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef,
    @Inject(ENVIRONMENT_CONFIG) private config: EnvironmentConfig
  ) {
    if (auth.currentUser && auth.currentUser.id_token) {
      const idToken = auth.currentUser.id_token;
      const payload = JSON.parse(atob(idToken.split('.')[1]));
      const samlAttributes = payload['saml_attributes'];

      if (
        (payload['email'].includes('spglobal.com') ||
          payload['email'].includes('ihsmarkit.com')) &&
        (!samlAttributes ||
          !Array.isArray(samlAttributes.MemberOf) ||
          samlAttributes.MemberOf.length === 0 ||
          !samlAttributes.MemberOf.includes(environment.groupName))
      ) {
        this.auth.startSignoutMainWindow();
        return;
      }
      this.user.next(this.auth.currentUser);
      this.token.next(this.auth.currentUser.access_token);
    }

    auth.userLoadedEvent.subscribe((user: User) => {
      this.user.next(user);
      this.token.next(user.access_token);
    });

    this.cupcakeMajorVersion = config.northstar.cupcakeMajorVersion;

    this.serverInfoSub = this.clearServerMenuItem();
  }

  public logout() {
    this.auth.startSignoutMainWindow();
  }

  public showClassificationNotice() {
    if (this.showClassificationModal) {
      return;
    }

    this.showClassificationModal = true;
  }

  public closeClassificationModal() {
    if (!this.showClassificationModal) {
      return;
    }

    this.showClassificationModal = false;
  }
  
  // public openUserSettings() {
  //   this.router.navigate(['/user-settings']);
  // }

  public ngOnInit() {
    this.isLoading = this.router.events.pipe(
      filter(
        (events) =>
          events instanceof RouteConfigLoadStart ||
          events instanceof NavigationEnd ||
          events instanceof NavigationCancel
      ),
      map((event) => {
        return event instanceof RouteConfigLoadStart;
      })
    );

    // Initialize footer links
    this.initializeFooterLinks();

    // Track footer label based on current route
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.currentFooterLabel = this.getFooterLabelForRoute(
          event.urlAfterRedirects || event.url
        );
        this.updateFooterLinks();
      });

    // Set initial footer label
    this.currentFooterLabel = this.getFooterLabelForRoute(
      this.getCurrentUrlForFooterLabel()
    );
    this.updateFooterLinks();

    this.browserBackSub = fromEvent(window, 'popstate').subscribe(() => {
      if (this.showClassificationModal) {
        this.closeClassificationModal();
      }
    });

    this.polarisRouting.start();
  }

  public ngOnDestroy(): void {
    if (this.serverInfoSub) {
      this.serverInfoSub.unsubscribe();
    }

    if (this.browserBackSub) {
      this.browserBackSub.unsubscribe();
    }
  }

  ngAfterViewInit() {
    // Setup click handler for classification notice link with delay to ensure footer is rendered
    setTimeout(() => {
      document.addEventListener('click', (event: MouseEvent) => {
          const target = event.target as HTMLElement;
          const link = target.closest('a[href="#open-classification-notice"]');
          if (link) {
            event.preventDefault();
            event.stopPropagation();
            this.showClassificationNotice();
            this.cdr.detectChanges();
          }
      }, true);
    }, 100);
  }

  private initializeFooterLinks() {
    // Get default links from spr-footer and append classification label
    this.footerLinks = [
      {
        name: 'Corporate Privacy Policy',
        href: 'https://www.spglobal.com/en/privacy/privacy-policy-english',
        openInNewTab: true,
      },
      {
        name: 'Terms of Use',
        href: 'https://www.spglobal.com/en/terms-of-use',
        openInNewTab: true,
      },
      {
        name: 'Cookie Notice',
        href: 'https://www.spglobal.com/en/cookie-notice',
        openInNewTab: true,
      },
      {
        name: 'Client Privacy Portal',
        href: 'https://www.spglobal.com/en/enterprise/Legal/privacy.html',
        openInNewTab: true,
      },
      {
        name: 'Do Not Sell my Personal Information',
        href: 'https://more.spglobal.com/DoNotSell',
        openInNewTab: true,
      },
      {
        name: this.currentFooterLabel,
        href: '#open-classification-notice',
      },
    ];
  }

  private updateFooterLinks() {
    // Create a new array reference to trigger Angular change detection
    if (this.footerLinks && this.footerLinks.length > 0) {
      this.footerLinks = [
        ...this.footerLinks.slice(0, -1),
        {
          name: this.currentFooterLabel,
          href: '#open-classification-notice',
        },
      ];
    }
  }

  private convertNavToHeaderItems(navigationItems: Navigation[]) {
    return navigationItems.map((navItem) => {
      if (navItem.children && navItem.children.length > 0) {
        return {
          type: HeaderItemType.dropdown,
          group:
            navItem.name === 'Munis' ? HeaderGroup.center : HeaderGroup.right,
          params: {
            name: navItem.name,
            icon: navItem.icon,
            path: navItem.path,
            menuItems: navItem.children.map((childNavItem) => {
              return {
                name: childNavItem.name,
                icon: childNavItem.icon,
                path: childNavItem.path,
              };
            }),
          },
        };
      } else {
        return {
          type: HeaderItemType.link,
          group: navItem.name.startsWith('Deal Query (')
            ? HeaderGroup.right
            : HeaderGroup.center,
          params: {
            name: navItem.name,
            icon: navItem.icon,
            path: navItem.path,
          },
        };
      }
    });
  }

  private getFooterLabelForRoute(url: string): string {
    const normalizedUrl = (url || '').toLowerCase();

    // Check if route is Street Calendar (Public), otherwise Confidential
    if (this.isStreetCalendarRoute(normalizedUrl)) {
      return 'Public';
    }

    return 'Confidential'; // Default label
  }

  private getCurrentUrlForFooterLabel(): string {
    if (this.router.url && this.router.url !== '/') {
      return this.router.url;
    }

    return `${window.location.pathname}${window.location.search}${window.location.hash}`;
  }

  private isStreetCalendarRoute(url: string): boolean {
    return /(^|\/)munis\/deals\/calendar($|[/?#;])/.test(url);
  }

  private clearServerMenuItem() {
    return combineLatest([
      this.featureRoutes,
      this.serverInfoObs,
      this.router.events.pipe(filter((e) => e instanceof RoutesRecognized && !EXCLUDED_ROUTES.includes(e.url))),
    ])
      .pipe(
        map(
          ([featureRoutes, serverInfo, routeInfo]: [
            RouteManifest[],
            DevServer[],
            RoutesRecognized
          ]) => {
            let loadedServerInfo = {
              name: '',
              env: '',
              releaseVersion: '',
            };
            const currRoute = Object.prototype.hasOwnProperty.call(
              routeInfo,
              'url'
            )
              ? routeInfo.url.split('?')[0]
              : undefined;

            if (
              featureRoutes &&
              featureRoutes[0]?.module?.params?.platform &&
              currRoute !== '/'
            ) {
              const matchingCurrRoute = featureRoutes.find((route) =>
                currRoute.includes(route.path)
              );
              loadedServerInfo = {
                name: matchingCurrRoute.module.params.platform as string,
                env: matchingCurrRoute.module.params.env as any,
                releaseVersion: matchingCurrRoute.module.version,
              };
            }

            if (serverInfo[0]) {
              const idServer = serverInfo[0].url.split('/');
              let name, env, releaseVersion;

              if (idServer.some((ea) => ea === 'local.muni.ipreo.com:444')) {
                name = idServer[3].split('-')[0];
                env = 'local';
                releaseVersion = '';
              } else if (
                idServer.some(
                  (ea) => ea === 'packages.dev.muni.globalmarkets.ihsmarkit.com'
                )
              ) {
                name = idServer[4].split('-')[0];
                env = idServer[4].split('-')[1].split('.')[0];
                releaseVersion = '';
              }

              loadedServerInfo = {
                name,
                env,
                releaseVersion,
              };
            }

            return loadedServerInfo;
          }
        )
      )
      .subscribe((loadedServerInfo) => {
        const serverName = !loadedServerInfo.name
          ? null
          : `${loadedServerInfo.name} - ${loadedServerInfo.env} ${loadedServerInfo.releaseVersion.length
                ? loadedServerInfo.releaseVersion
                : ''
            }`;
        const serverMenuItem = () => {
          if (!serverName) {
            return null;
          }
          if (loadedServerInfo.name === 'sellside') {
            return {
              type: HeaderItemType.dropdown,
              group: HeaderGroup.right,
              params: {
                name: serverName,
                menuItems: [
                  {
                    name: `Clear Dev Server`,
                    event: () => {
                      this.polarisRouting.start(true);
                    },
                  },
                ],
              },
            };
          } else {
            return {
              type: HeaderItemType.info,
              group: HeaderGroup.right,
              params: {
                name: serverName,
              },
            };
          }
        };

        this.serverInfo.next(serverMenuItem());
      });
  }
}
